/**
 * AppOverlayHost
 *
 * Single app-level host for the two full-screen blocking overlays plus the
 * "legal document updated" modal queue. Mounted once in `AppRoutes` (next to
 * `VideoUploadProgressOverlay`), inside `RootScreen` and outside `<Routes>`, so
 * it survives route changes — the web equivalent of Flutter inserting an
 * `OverlayEntry` into the root `Overlay`.
 *
 * Flutter source of truth: `root_screen.dart:306-320` — the `AppOverlayNotifier`
 * listener inserts `AppOverlay.budgetExceededOverlay()` (`:308-312`) or
 * `AppOverlay.accountSuspendedOverlay()` (`:314-318`) and then calls
 * `pauseAllVideos()` in **both** branches (`:310-312`, `:316-318`). The web
 * equivalent of that side effect is `videoElementRegistry.pauseAll()`, already
 * the app-lifecycle pause path (`videoManagementSlice.ts:64`).
 *
 * Precedence: the suspended overlay wins if both flags are somehow true — it is
 * the terminal state, and unlike the budget overlay it cannot be dismissed. The
 * legal-document modal comes last: it is informational, so it waits until
 * neither blocking overlay is up rather than stacking on top of one.
 *
 * Only the **queue head** is rendered (`selectPendingLegalDocument`), so a push
 * that bumps several `version*` keys presents its modals one after another —
 * Flutter is sequential for the same reason, awaiting each
 * `showInfoWidgetDialog` inside its update loop
 * (`remote_config_service.dart:234-310`).
 *
 * ## Route suppression for the account-suspended overlay
 *
 * The suspended overlay is terminal and un-dismissible, and its only control
 * ("Contact us") navigates to the public `/contact` page. That control does
 * **not** clear `accountSuspendedVisible` — doing so would unblock the whole
 * app the moment the user navigated back, which is exactly the escape a user
 * reported. Instead the flag stays raised for the whole suspended session and
 * this host suppresses the overlay only while the current route is one of
 * `APP_OVERLAY_CONSTANTS.accountSuspendedExemptPaths` (see
 * `isAccountSuspendedExemptPath`). Every other route re-blocks automatically,
 * with no page-lifecycle wiring anywhere.
 *
 * Flutter needs no such rule: its `OverlayEntry` is imperatively removed before
 * the Contact push and re-inserted when that push pops
 * (`overlay_widget.dart:154-179`), so the entry itself carries the state. The
 * web reaches the equivalent outcome declaratively, with these bounded
 * differences — neither mechanism dominates the other:
 *
 * - Flutter's removal is **push-scoped**: the entry is gone for the entire
 *   duration of `NavigationService.navigateToContactScreen()`
 *   (`navigation_service.dart:1574`), so anything stacked above Contact is
 *   uncovered too. The web's is **route-scoped**: it stands down on exactly the
 *   exempt paths. The exempt set covers the escape flow's full reachable
 *   closure on web — `/contact` → `/support` → `/legal/privacy-policy`
 *   (`ContactSupportSection.tsx:26`, `SupportItemPage.tsx:162-167`) — so for
 *   every path a user can take *out of this overlay* the two mechanisms agree
 *   on whether the overlay shows.
 * - Route-scoped is safer in the **forward** direction: a future page reachable
 *   from Contact that is not in the exempt set re-blocks, where Flutter's
 *   push-scoped removal would leave it uncovered until the push pops.
 * - Route-scoped is more permissive in the **entry** direction: suppression is
 *   unconditional on the exempt paths rather than scoped to the escape flow, so
 *   a suspended user who never touches the Contact-us control — deep link,
 *   typed URL or bookmark straight to `/contact`, `/support` or
 *   `/legal/privacy-policy` while `AppInitializer.tsx:91` raises the flag on
 *   startup — sees no overlay there, whereas Flutter's entry
 *   (`root_screen.dart:315`) covers its Contact screen too and stands down only
 *   on the button handler's explicit `remove()`. That gap also defers the
 *   paired side effect: `root_screen.dart:315-318` inserts and then calls
 *   `pauseAllVideos()` unconditionally, while here `videoElementRegistry
 *   .pauseAll()` does not fire on that render and fires instead on the
 *   false→true edge when the user leaves the exempt route. Nothing plays there
 *   in practice — `/contact`, `/support` and `/legal/*` host no video surface —
 *   and the whole exempt set is `PUBLIC_ROUTES` (`routes.config.tsx:80-150`),
 *   reachable signed-out, so it grants a suspended user nothing.
 *
 * Flutter *mobile*'s support item screen is a bare `WebViewController`
 * (`lib/presentation/screens/legal/support_item_screen.dart:20`) loading an
 * external URL (`:83`), with no in-app privacy-policy push, so it has no
 * equivalent of the web's `/support` → `/legal/privacy-policy` hop to diverge
 * on. With `/legal` exempt the web matches Flutter's push-scoped removal for
 * that hop regardless.
 */

import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { APP_OVERLAY_CONSTANTS } from '@/presentation/constants/appOverlayConstants';
import { videoElementRegistry } from '@/presentation/registries/videoElementRegistry';
import { setBodyScrollLock } from '@/presentation/utils/bodyScrollLock';
import type { AppDispatch } from '@/presentation/store';
import {
  dismissUpdatedLegalDocument,
  selectAccountSuspendedVisible,
  selectBudgetExceededVisible,
  selectPendingLegalDocument,
} from '@/presentation/store/slices/appOverlay/appOverlaySlice';
import { AccountSuspendedOverlay } from './AccountSuspendedOverlay';
import { BudgetExceededOverlay } from './BudgetExceededOverlay';
import { LegalDocumentUpdatedModal } from './LegalDocumentUpdatedModal';

/**
 * True while the current route is one the account-suspended overlay stands
 * down for — the escape hatch its "Contact us" control opens. Matches the exact
 * path or any sub-path (`/support/12`, `/legal/privacy-policy`), never a prefix
 * collision (`/supportive`).
 */
const isAccountSuspendedExemptPath = (pathname: string): boolean =>
  APP_OVERLAY_CONSTANTS.accountSuspendedExemptPaths.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );

export const AppOverlayHost: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  // Safe here: this component is mounted at `AppRoutes.tsx:139`, inside
  // `<Router>` (`:108`) and outside `<Routes>` (`:140`), so it has a router
  // context and still survives route changes.
  const location = useLocation();
  const budgetExceededVisible = useSelector(selectBudgetExceededVisible);
  const accountSuspendedVisible = useSelector(selectAccountSuspendedVisible);
  const pendingLegalDocument = useSelector(selectPendingLegalDocument);

  const accountSuspendedSuppressed =
    accountSuspendedVisible && isAccountSuspendedExemptPath(location.pathname);

  // Precedence is unchanged — suspended > budget > legal modal. Both lower
  // surfaces stay gated on the raw `accountSuspendedVisible` flag, so a
  // *suppressed* suspended overlay deliberately does NOT fall through to the
  // budget overlay **or the legal-document modal**: the user is still
  // suspended, and the escape route must not be re-covered by another surface.
  // Nothing is lost — the queue is only drained by the rendered modal's
  // `onClose`, so a document queued while suppressed re-presents as soon as the
  // user leaves the exempt route. Flutter differs on the modal alone:
  // `NavigationService.showInfoWidgetDialog` (`navigation_service.dart:1880`)
  // pushes a `CupertinoDialogRoute` onto the Navigator, below the root
  // `Overlay` entries, so it is visible there during the Contact push.
  const showAccountSuspended = accountSuspendedVisible && !accountSuspendedSuppressed;
  const showBudgetExceeded = !accountSuspendedVisible && budgetExceededVisible;
  const showLegalDocument =
    !accountSuspendedVisible && !budgetExceededVisible && pendingLegalDocument !== null;

  const anyOverlayVisible = showAccountSuspended || showBudgetExceeded;
  const anySurfaceVisible = anyOverlayVisible || showLegalDocument;

  const onDismissLegalDocument = useCallback(() => {
    dispatch(dismissUpdatedLegalDocument());
  }, [dispatch]);

  // Flutter pauses every playing video right after inserting either overlay
  // (`root_screen.dart:310-312`, `:316-318`). Keyed on the combined visibility
  // so it fires on the false→true edge only — hiding the budget overlay
  // re-renders with `anyOverlayVisible === false` and pauses nothing. Route
  // suppression rides the same edge: leaving an exempt route brings the
  // suspended overlay back and re-fires the pause, matching Flutter pairing the
  // re-insert with `pauseAllVideos()` (`overlay_widget.dart:167-177`).
  useEffect(() => {
    if (anyOverlayVisible) {
      videoElementRegistry.pauseAll();
    }
  }, [anyOverlayVisible]);

  // These surfaces bypass `SheetService` on purpose (see the component headers),
  // so they must reproduce the scroll/selection lock `ExpauseSheet` applies to
  // every sheet and dialog. Without it the page keeps scrolling — and stays
  // selectable — behind a full-screen blocking overlay. The lock goes through
  // the shared owner-keyed helper rather than writing `document.body.style`
  // directly: an overlay can be raised from *inside* an open sheet (e.g.
  // `AddContentSheet.show`'s budget gate, which dispatches and returns without
  // opening its nested sheet), so releasing on hide/unmount must not drop the
  // sheet's still-required lock.
  useEffect(() => {
    if (!anySurfaceVisible) {
      return;
    }

    setBodyScrollLock('appOverlayHost', true);

    return () => {
      setBodyScrollLock('appOverlayHost', false);
    };
  }, [anySurfaceVisible]);

  if (showAccountSuspended) {
    return <AccountSuspendedOverlay />;
  }

  if (showBudgetExceeded) {
    return <BudgetExceededOverlay />;
  }

  // The redundant-looking null check is the TS narrowing for the prop.
  if (showLegalDocument && pendingLegalDocument !== null) {
    return (
      <LegalDocumentUpdatedModal
        legalDocument={pendingLegalDocument}
        onClose={onDismissLegalDocument}
      />
    );
  }

  return null;
};
